import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Icon } from "../../components/tailwind";
import { RootState } from "../../utils/store";
import { FilterOptions, getFilterOptions } from "../../api/openrouterApi";
import { CheckboxFilter } from "./CheckboxFilter";

type Props = {
  search: string;
  providers: string[];
  supportedParameters: string[];
  onSearchChange: (search: string) => void;
  onProvidersChange: (providers: string[]) => void;
  onSupportedParametersChange: (params: string[]) => void;
  onClearFilters: () => void;
};

export const ModelFilters: React.FC<Props> = ({
  search,
  providers,
  supportedParameters,
  onSearchChange,
  onProvidersChange,
  onSupportedParametersChange,
  onClearFilters,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(
    null
  );
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [localSearch, setLocalSearch] = useState(search);

  // Sync local search state when search prop changes (e.g., when cleared)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    const fetchOptions = async (): Promise<void> => {
      if (!token) return;
      try {
        setLoadingOptions(true);
        const options = await getFilterOptions(token);
        setFilterOptions(options);
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [token]);

  const hasActiveFilters =
    search.trim() !== "" ||
    providers.length > 0 ||
    supportedParameters.length > 0;

  return (
    <div className="tw-mb-6 tw-space-y-4">
      {/* Search Bar - Own Row */}
      <div>
        <form
          onSubmit={(e): void => {
            e.preventDefault();
            onSearchChange(localSearch);
          }}
          className="tw-flex tw-gap-2 tw-max-w-md"
        >
          <input
            name="search"
            type="text"
            value={localSearch}
            onChange={(e): void => setLocalSearch(e.target.value)}
            placeholder="Search by model name..."
            className="tw-flex-1 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-border-transparent"
          />
          <Button type="submit" variant="primary" size="sm" icon>
            <Icon name="search" />
            Search
          </Button>
        </form>
      </div>

      {/* Filters - Grid Layout */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-items-end">
        {/* Provider Filter */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Provider
          </label>
          <CheckboxFilter
            options={filterOptions?.providers || []}
            selected={providers}
            onChange={onProvidersChange}
            placeholder="Select providers..."
            disabled={loadingOptions}
            testIdBase="provider-filter"
          />
        </div>

        {/* Supported Parameters Filter */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Supported Parameters
          </label>
          <CheckboxFilter
            options={filterOptions?.supported_parameters || []}
            selected={supportedParameters}
            onChange={onSupportedParametersChange}
            placeholder="Select parameters..."
            disabled={loadingOptions}
            testIdBase="parameter-filter"
          />
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div>
            <Button
              variant="secondary-alt"
              size="sm"
              icon
              onClick={onClearFilters}
            >
              <Icon name="delete" />
              Clear filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
